import React, { useState, useEffect, useRef } from 'react';
import { ArrowLeft, Upload, Plus, X, Star, Package, Search, Info, Calculator, LayoutGrid, FileText, BadgeCheck, IndianRupee, HelpCircle } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useTheme } from '../context/ThemeContext';
import { message, Select, Modal } from 'antd';
import { productsApi } from '../services/api';
import { ASSET_BASE_URL as ASSET_URL } from '../services/api';

const RatingStar = ({ index, rating, onClick }) => {
  const percentage = Math.max(0, Math.min(100, (rating - index) * 100));
  const gradId = `star-grad-${index}`;

  return (
    <svg
      width="24"
      height="24"
      viewBox="0 0 24 24"
      onClick={() => onClick(index + 1)}
      className="cursor-pointer transition-all duration-200 hover:scale-110 active:scale-95 drop-shadow-sm"
    >
      <defs>
        <linearGradient id={gradId} x1="0%" y1="0%" x2="100%" y2="0%">
          <stop offset={`${percentage}%`} stopColor="#facc15" />
          <stop offset={`${percentage}%`} stopColor="#e5e7eb" />
        </linearGradient>
      </defs>
      <path
        d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z"
        fill={`url(#${gradId})`}
        stroke={percentage > 0 ? "#eab308" : "#d1d5db"}
        strokeWidth="1"
        strokeLinejoin="round"
      />
    </svg>
  );
};

const AddProduct = () => {
  const navigate = useNavigate();
  const { currentTheme } = useTheme();

  const [productType, setProductType] = useState('product'); // 'product' or 'kit'
  const [isKitStarted, setIsKitStarted] = useState(false);
  const [selectedProducts, setSelectedProducts] = useState([]);
  const [isEditMode, setIsEditMode] = useState(false);
  const [editingProductId, setEditingProductId] = useState(null);

  const [availableProducts, setAvailableProducts] = useState([]);
  const [showProductSelectModal, setShowProductSelectModal] = useState(false);
  const [activeKitProductId, setActiveKitProductId] = useState(null);
  const [productSearchTerm, setProductSearchTerm] = useState('');
  const [tempSelectedIds, setTempSelectedIds] = useState([]); // For bulk selection modal

  const [formData, setFormData] = useState({
    title: '',
    name: '',
    category: '',
    subCategory: '',
    url: '',
    packageSize: '',
    ratings: 0,
    aboutProduct: '',
    description: '',
    mrp: '',
    batchNumber: '',
    hsnCode: '',
    tax: '',
    manufactoryDate: '',
    expiryDate: '',
    pts: '',
    ptr: '',
    ptd: '',
    sku: '',
    b2bDiscount: '',
    b2cDiscount: '',
    altText: '',
    range: '',
    tags: [],
    attributes: [],
    ingredients: [],
    faqs: []
  });

  const [productImages, setProductImages] = useState([]);
  const [tempTag, setTempTag] = useState('');
  const [tempAttribute, setTempAttribute] = useState('');
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);

  // Refs for sticky navigation
  const detailsRef = useRef(null);
  const contentRef = useRef(null);
  const benefitsRef = useRef(null);
  const pricingRef = useRef(null);
  const faqRef = useRef(null);
  const searchContainerRef = useRef(null);
  const [categories, setCategories] = useState([]);
  const [subCategories, setSubCategories] = useState([]); // loaded from API on mount
  const [productImageFile, setProductImageFile] = useState(null);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (searchContainerRef.current && !searchContainerRef.current.contains(event.target)) {
        setProductSearchTerm('');
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const [activeTab, setActiveTab] = useState('details');

  const scrollToSection = (tabId) => {
    setActiveTab(tabId);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  // Price Calculation Logic
  const calculateSellingPrice = () => {
    const mrp = parseFloat(String(formData.mrp).replace(/[₹,\s]/g, '')) || 0;
    const discount = parseFloat(String(formData.b2cDiscount).replace(/[%\s]/g, '')) || 0;
    const saving = (mrp * discount) / 100;
    const sellingPrice = mrp - saving;
    return {
      sellingPrice: sellingPrice.toFixed(2),
      saving: saving.toFixed(2)
    };
  };

  const priceStats = calculateSellingPrice();

  // Progress Bar Calculation
  const calculateProgress = () => {
    const mandatoryFields = ['title', 'name', 'subCategory', 'url', 'packageSize', 'ratings', 'description', 'mrp', 'batchNumber', 'hsnCode', 'sku', 'tax', 'manufactoryDate', 'expiryDate'];
    const filledFields = mandatoryFields.filter(field => formData[field] && String(formData[field]).trim() !== '');
    const imageProgress = productImages.length > 0 ? 1 : 0;
    const totalMandatory = mandatoryFields.length + 1; // +1 for image
    return Math.round(((filledFields.length + imageProgress) / totalMandatory) * 100);
  };

  const progress = calculateProgress();

  const [isDragging, setIsDragging] = useState(false);

  const handleDragOver = (e) => {
    e.preventDefault();
    setIsDragging(true);
  };

  const handleDragLeave = () => {
    setIsDragging(false);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setIsDragging(false);
    const files = Array.from(e.dataTransfer.files)
        .filter(f => f.type.startsWith('image/'));
    if (files.length > 0) {
        setProductImageFile(files[0]);
        files.forEach(file => {
            const reader = new FileReader();
            reader.onloadend = () => {
                setProductImages(prev => [...prev, reader.result].slice(0, 5));
            };
            reader.readAsDataURL(file);
        });
    }
  };

  // Helper function to format date for backend
  const formatDateForBackend = (dateString) => {
    if (!dateString) return null;
    try {
      const date = new Date(dateString);
      if (isNaN(date.getTime())) return null;
      // Format: YYYY-MM-DDTHH:mm:ss (LocalDateTime format)
      return date.toISOString().slice(0, 19);
    } catch (e) {
      console.error('Date formatting error:', e);
      return null;
    }
  };

  // Helper function to clean product data
  const cleanProductData = (data) => {
    const cleaned = { ...data };

    // Remove null, undefined, and empty strings
    Object.keys(cleaned).forEach(key => {
      if (cleaned[key] === null || cleaned[key] === undefined || cleaned[key] === '' || cleaned[key] === 'N/A') {
        delete cleaned[key];
      }
    });

    return cleaned;
  };

  // Load categories and editing product data on mount
  // Load data on mount
  useEffect(() => {
    const loadInitialData = async () => {
      try {
        // Categories dropdown
        const catRes = await productsApi.getActiveProductCategories();
        const list = Array.isArray(catRes) ? catRes : (catRes.data || []);
        const categoryNames = list.map(c => c.categoryName || c.name || c);
        setCategories(categoryNames);

        // Sub Category dropdown — GET /api/products/categories
        const allCatRes = await productsApi.getAllCategories().catch(() => null);
        let subList = [];

        if (allCatRes) {
          const raw = Array.isArray(allCatRes) ? allCatRes
            : Array.isArray(allCatRes.data) ? allCatRes.data
              : Array.isArray(allCatRes.categories) ? allCatRes.categories
                : [];

          // Try: each category has a .subcategories array
          raw.forEach(cat => {
            if (Array.isArray(cat.subcategories)) {
              cat.subcategories.forEach(sub => {
                const name = sub.subCategoryName || sub.name || sub;
                if (name && !subList.includes(name)) subList.push(name);
              });
            } else if (Array.isArray(cat.subCategories)) {
              cat.subCategories.forEach(sub => {
                const name = sub.subCategoryName || sub.name || sub;
                if (name && !subList.includes(name)) subList.push(name);
              });
            }
          });

          // Fallback: if no nested subcategories, use category names themselves
          if (subList.length === 0) {
            subList = raw
              .map(c => c.categoryName || c.name || (typeof c === 'string' ? c : null))
              .filter(Boolean);
          }
        }

        // Last resort fallback
        if (subList.length === 0) {
          subList = ['Syrup', 'Tablet', 'Capsule', 'Oil', 'Cream'];
        }

        setSubCategories(subList);

        // Load products for Kit Composition — Search & Bulk Select
        try {
          const prodRes = await productsApi.getAllProducts();
          const prodList = Array.isArray(prodRes) ? prodRes
            : Array.isArray(prodRes.data) ? prodRes.data
              : Array.isArray(prodRes.products) ? prodRes.products
                : [];
          setAvailableProducts(prodList.map(p => ({
            ...p,
            id: p.id || p._id,
            name: p.productName || p.name || 'Untitled',
            image: p.imageUrl || p.image || '',
            price: p.mrp || p.price || 0
          })));
        } catch (e) {
          console.error('Error loading products for kit composition:', e);
          setAvailableProducts([]);
        }
      } catch (err) {
        console.error('Error loading initial data:', err);
        setSubCategories(['Syrup', 'Tablet', 'Capsule', 'Oil', 'Cream']);
      }
    };

    loadInitialData();

    // Restore Edit Mode logic
    const editingProductContent = localStorage.getItem('editingProduct');
    if (editingProductContent) {
      try {
        const product = JSON.parse(editingProductContent);
        setIsEditMode(true);
        setEditingProductId(product.id || product._id);
        const type = product.kitItems ? 'kit' : (product.productType || 'product');
        setProductType(type);

        if (type === 'kit') {
          setIsKitStarted(true);
          // Set selection for kits
          if (product.kitItems && Array.isArray(product.kitItems)) {
            setSelectedProducts(product.kitItems.map(item => ({
              id: item.productId?.id || item.productId || Math.random(),
              productId: item.productId?.id || item.productId,
              name: item.productName || item.productId?.productName || 'Added Product',
              image: item.imageUrl || item.productId?.imageUrl || '',
              price: item.price || item.productId?.price || 0,
              quantity: item.quantity || 1
            })));
          }
        }

        // Set Image
        const img = product.kitImageUrl || product.imageUrl || product.image;
        if (img) setProductImages([img]);

        setFormData({
          title: product.kitName || product.title || product.productName || '',
          name: product.productName || product.kitName || product.name || '',
          category: product.category || '',
          subCategory: product.subCategory || '',
          url: product.productUrl || product.url || '',
          packageSize: product.packageSize || '',
          ratings: product.rating || product.ratings || 0,
          aboutProduct: product.aboutProduct || '',
          description: product.description || '',
          mrp: product.kitPrice || product.mrp || product.price || '',
          batchNumber: product.batchNumber || '',
          hsnCode: product.hsnCode || '',
          tax: product.tax || '',
          manufactoryDate: product.manufactoryDate || '',
          expiryDate: product.expiryDate || '',
          pts: product.pts || '',
          ptr: product.ptr || '',
          ptd: product.ptd || '',
          sku: product.sku || '',
          b2bDiscount: product.b2bDiscount || '',
          b2cDiscount: product.kitDiscountPercentage || product.b2cDiscount || '',
          altText: product.altText || '',
          range: product.range || '',
          tags: product.tags || [],
          attributes: product.attributes || [],
          ingredients: product.ingredients || [],
          faqs: product.faqs || []
        });

        // Fetch subcategories for the loaded category
        if (product.category) {
          productsApi.getSubCategories(product.category).then(subRes => {
            let subList = [];
            if (Array.isArray(subRes)) subList = subRes;
            else if (subRes && subRes.data && Array.isArray(subRes.data)) subList = subRes.data;
            else if (subRes && typeof subRes === 'object') subList = subRes.subcategories || subRes.list || [];
            const names = subList.map(s => s.subCategoryName || s.name || s);
            setSubCategories(names);
          }).catch(e => console.error('Error fetching subs in edit:', e));
        }

        // Only clear from localStorage after data is restored to state
        localStorage.removeItem('editingProduct');
      } catch (err) {
        console.error('Error parsing editing product:', err);
      }
    }
  }, []);

  const handleInputChange = (field, value) => {
    if (field === 'name') {
      setFormData(prev => ({ ...prev, [field]: value }));

      if (value.trim().length >= 3) {
        setErrors(prev => {
          const newErr = { ...prev };
          delete newErr.name;
          return newErr;
        });
      } else if (value.trim().length > 0) {
        setErrors(prev => ({ ...prev, name: 'Name must be at least 3 characters' }));
      }
    } else if (field === 'category') {
      setFormData(prev => ({ ...prev, category: value, subCategory: '' }));
      if (value) {
        const fetchSubs = async () => {
          try {
            const response = await productsApi.getSubCategories(value);
            let subList = [];
            if (Array.isArray(response)) subList = response;
            else if (response?.data && Array.isArray(response.data)) subList = response.data;
            else if (response && typeof response === 'object') subList = response.subcategories || response.list || [];

            // ✅ subCategoryName ya name dono handle karo
            const names = subList.map(s => s.subCategoryName || s.name || s).filter(Boolean);
            setSubCategories(names.length > 0 ? names : []);
          } catch (err) {
            console.error('Error fetching subcategories:', err);
            setSubCategories([]);
          }
        };
        fetchSubs();
      } else {
        setSubCategories([]);
      }
    } else if (field === 'url') {
      setFormData(prev => ({ ...prev, [field]: value }));
      const urlRegex = /^(https?:\/\/)?([\da-z.-]+)\.([a-z.]{2,6})([/\w .-]*)*\/?$/;
      if (!value) setErrors(prev => ({ ...prev, url: 'Product URL is required' }));
      else if (!urlRegex.test(value)) setErrors(prev => ({ ...prev, url: 'Please enter a valid URL' }));
      else {
        setErrors(prev => {
          const newErr = { ...prev };
          delete newErr.url;
          return newErr;
        });
      }
    } else if (['mrp', 'pts', 'ptr', 'ptd', 'tax', 'b2bDiscount', 'b2cDiscount'].includes(field)) {
      const cleanedValue = value.replace(/[^0-9.]/g, '');
      const decimalCount = (cleanedValue.match(/\./g) || []).length;
      if (decimalCount > 1) return;

      setFormData(prev => ({ ...prev, [field]: cleanedValue }));

      if (['tax', 'b2bDiscount', 'b2cDiscount'].includes(field)) {
        const val = parseFloat(cleanedValue);
        if (val > 100) {
          setErrors(prev => ({ ...prev, [field]: `${field.charAt(0).toUpperCase() + field.slice(1)} cannot exceed 100%` }));
        } else {
          setErrors(prev => {
            const newErr = { ...prev };
            delete newErr[field];
            return newErr;
          });
        }
      } else {
        setErrors(prev => {
          const newErr = { ...prev };
          delete newErr[field];
          return newErr;
        });
      }
    } else if (field === 'hsnCode') {
      setFormData(prev => ({ ...prev, [field]: value }));
      setErrors(prev => {
        const newErr = { ...prev };
        delete newErr.hsnCode;
        return newErr;
      });
    } else if (field === 'manufactoryDate' || field === 'expiryDate') {
      setFormData(prev => {
        const newData = { ...prev, [field]: value };
        if (field === 'manufactoryDate') {
          if (new Date(value) > new Date()) {
            setErrors(e => ({ ...e, manufactoryDate: 'Date cannot be in the future' }));
          } else {
            setErrors(e => {
              const res = { ...e };
              delete res.manufactoryDate;
              return res;
            });
          }
        }
        if (newData.manufactoryDate && newData.expiryDate) {
          if (new Date(newData.expiryDate) <= new Date(newData.manufactoryDate)) {
            setErrors(e => ({ ...e, expiryDate: 'Expiry must be after manufacturing date' }));
          } else {
            setErrors(e => {
              const res = { ...e };
              delete res.expiryDate;
              return res;
            });
          }
        }
        return newData;
      });
    } else if (field === 'packageSize' || field === 'subCategory' || field === 'ratings') {
      setFormData(prev => ({ ...prev, [field]: value }));
      if (!value) {
        let errorMsg = '';
        if (field === 'subCategory') errorMsg = 'Sub Category is required';
        else if (field === 'packageSize') errorMsg = 'Package size is required';
        else if (field === 'ratings') errorMsg = 'Please provide a rating';

        setErrors(prev => ({ ...prev, [field]: errorMsg }));
      } else {
        setErrors(prev => {
          const newErr = { ...prev };
          delete newErr[field];
          return newErr;
        });
      }
    } else {
      setFormData(prev => ({ ...prev, [field]: value }));
      if (value) {
        setErrors(prev => {
          const newErr = { ...prev };
          delete newErr[field];
          return newErr;
        });
      }
    }
  };

  // ✅ Blob URL ki jagah base64 use karo
  const handleFileUpload = (e) => {
    const files = Array.from(e.target.files);
    if (files.length > 0) {
      setProductImageFile(files[0]); // API ke liye actual file
      files.forEach(file => {
          const reader = new FileReader();
          reader.onloadend = () => {
              setProductImages(prev => [...prev, reader.result].slice(0, 5));
          };
          reader.readAsDataURL(file);
      });
    }
  };

  const handleRemoveImage = (index) => {
    setProductImages(prev => prev.filter((_, i) => i !== index));
  };

  const handleAddTag = () => {
    if (tempTag.trim()) {
      setFormData(prev => ({ ...prev, tags: [...prev.tags, tempTag.trim()] }));
      setTempTag('');
    }
  };

  const handleRemoveTag = (index) => {
    setFormData(prev => ({ ...prev, tags: prev.tags.filter((_, i) => i !== index) }));
  };

  const handleAddAttribute = () => {
    if (tempAttribute.trim()) {
      setFormData(prev => ({ ...prev, attributes: [...prev.attributes, tempAttribute.trim()] }));
      setTempAttribute('');
    }
  };

  const handleRemoveAttribute = (index) => {
    setFormData(prev => ({ ...prev, attributes: prev.attributes.filter((_, i) => i !== index) }));
  };

  const handleAddIngredient = () => {
    setFormData(prev => ({
      ...prev,
      ingredients: [...prev.ingredients, { heading: '', content: '', imageUrl: '', previewUrl: '' }]
    }));
  };

  const handleRemoveIngredient = (index) => {
    setFormData(prev => ({
      ...prev,
      ingredients: prev.ingredients.filter((_, i) => i !== index)
    }));
  };

  const handleIngredientChange = async (index, field, value) => {
    if (field === 'imgFile') {
      try {
        message.loading({ content: 'Uploading image...', key: 'upload' });

        // Show local preview immediately
        const localUrl = URL.createObjectURL(value);
        const preIngredients = [...formData.ingredients];
        preIngredients[index]['previewUrl'] = localUrl;
        setFormData(prev => ({ ...prev, ingredients: preIngredients }));

        // Call API
        const response = await productsApi.uploadIngredientImage(value);

        // Use received image URL
        const finalUrl = response.imageUrl || response;
        const updatedIngredients = [...formData.ingredients];
        updatedIngredients[index]['imageUrl'] = finalUrl;
        setFormData(prev => ({ ...prev, ingredients: updatedIngredients }));

        message.success({ content: 'Image uploaded successfully!', key: 'upload' });
      } catch (err) {
        console.error('Upload error:', err);
        message.error({ content: 'Failed to process image', key: 'upload' });
      }
      return;
    }
    const newIngredients = [...formData.ingredients];
    newIngredients[index][field] = value;
    setFormData(prev => ({ ...prev, ingredients: newIngredients }));
  };

  const handleAddFAQ = () => {
    setFormData(prev => ({
      ...prev,
      faqs: [...prev.faqs, { question: '', answer: '' }]
    }));
  };

  const handleFAQChange = (index, field, value) => {
    const newFaqs = [...formData.faqs];
    newFaqs[index][field] = value;
    setFormData(prev => ({ ...prev, faqs: newFaqs }));
  };

  const handleAddProductToKit = () => {
    setSelectedProducts(prev => [...prev, {
      id: Date.now(),
      image: '',
      name: '',
      quantity: 1,
      altText: '',
      range: '',
      price: '',
      packageDetails: ''
    }]);
  };

  const handleRemoveProductFromKit = (id) => {
    setSelectedProducts(prev => prev.filter(p => p.id !== id));
  };

  const handleProductKitChange = (id, field, value) => {
    setSelectedProducts(prev => prev.map(p =>
      p.id === id ? { ...p, [field]: value } : p
    ));
  };

  const handleProductKitImageUpload = async (id, e) => {
    const file = e.target.files[0];
    if (file) {
      try {
        message.loading({ content: 'Uploading kit image...', key: 'uploadKit' });

        // Local preview
        const localUrl = URL.createObjectURL(file);
        setSelectedProducts(prev => prev.map(p =>
          p.id === id ? { ...p, image: localUrl } : p
        ));

        // Call API
        const response = await productsApi.uploadKitImage(file);
        const finalUrl = response.imageUrl || response;

        setSelectedProducts(prev => prev.map(p =>
          p.id === id ? { ...p, image: finalUrl } : p
        ));
        message.success({ content: 'Kit image uploaded successfully!', key: 'uploadKit' });
      } catch (err) {
        console.error('Kit image upload error:', err);
        message.error({ content: 'Failed to upload kit image', key: 'uploadKit' });
      }
    }
  };

  const handleSubmit = async () => {
    // Validation logic
    const newErrors = {};

    // Required & Basic Checks
    // Removed mandatory validation as per user request
    if (formData.name && formData.name.trim().length > 0 && formData.name.trim().length < 3) {
      newErrors.name = 'Name must be at least 3 characters';
    }
    if (!formData.ratings && formData.ratings !== 0) newErrors.ratings = 'Please provide a rating';
    if (!formData.description) newErrors.description = 'Description is required';

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      message.error('Please fix the errors in the form');
      return;
    }

    try {
      setLoading(true);

      if (productType === 'kit') {
        const kitData = {
          kitName: formData.title || formData.name || 'Untitled Kit',
          description: formData.description || '',
          kitImageUrl: productImages[0] || '',
          kitDiscountPercentage: parseFloat(formData.b2cDiscount) || 0,
          isActive: true,
          category: formData.category || 'AYURVEDIC',
          kitPrice: parseFloat(formData.mrp) || 0,
          kitItems: selectedProducts.map(p => ({
            productId: p.productId || p.id,
            quantity: parseInt(p.quantity) || 1
          })),
        };

        if (isEditMode) {
          await productsApi.updateKit(editingProductId, kitData);
          message.success('Kit updated successfully!');
        } else {
          await productsApi.createKit(kitData);
          message.success('Kit created successfully!');
        }

      } else {
        // ✅ Fix - sahi field names API docs se match karte hue
        const productData = {
          sku: formData.sku,
          productName: formData.name,
          title: formData.title,
          category: formData.category,
          subCategory: formData.subCategory,
          productUrl: formData.url,
          description: formData.description,
          price: parseFloat(formData.mrp) || 0,
          mrp: parseFloat(formData.mrp) || 0,
          imageUrl: productImages[0] || '',
          isActive: true,
          stockQuantity: 100,
          rating: parseFloat(formData.ratings) || 0,
          packageSize: formData.packageSize,
          aboutProduct: formData.aboutProduct,
          pts: parseFloat(formData.pts) || 0,
          ptr: parseFloat(formData.ptr) || 0,
          ptd: parseFloat(formData.ptd) || 0,
          b2bDiscount: parseFloat(formData.b2bDiscount) || 0,
          b2cDiscount: parseFloat(formData.b2cDiscount) || 0,
          gstPercentage: parseFloat(formData.tax) || 0,  // ✅ tax -> gstPercentage
          hsnCode: formData.hsnCode,
          batchNumber: formData.batchNumber,
          // ✅ Fix - date format T00:00:00 append karo
          manufactoryDate: formData.manufactoryDate          // ✅ backend spelling
            ? `${formData.manufactoryDate}T00:00:00`
            : null,
          expiryDate: formData.expiryDate
            ? `${formData.expiryDate}T00:00:00`
            : null,
          altText: formData.altText,
          range: formData.range,
          relatedTags: formData.tags,                       // ✅ tags -> relatedTags
          attributes: formData.attributes,
          ingredients: formData.ingredients.map(ing => ({
            heading: ing.heading || '',
            content: ing.content || '',
            imageUrl: ing.imageUrl || ''
          })),
          faqs: formData.faqs.map(faq => ({
            question: faq.question || '',
            answer: faq.answer || ''
          }))
        };

        const imageFileToUpload = productImageFile; // The actual file if they dropped one.

        if (isEditMode) {
          if (imageFileToUpload) {
            await productsApi.updateProduct(editingProductId, productData, imageFileToUpload);
          } else {
            await productsApi.updateProductJson(editingProductId, productData);
          }
          message.success('Product updated successfully!');
        } else {
          if (imageFileToUpload) {
            await productsApi.createProduct(productData, imageFileToUpload);
          } else {
            await productsApi.createProductJson(productData);
          }
          message.success('Product added successfully!');
        }
      }

      navigate('/products/list');

    } catch (err) {
      console.error('Error saving:', err);
      message.error('Failed to save product/kit');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen p-4 desktop:p-8" style={{ background: `linear-gradient(to bottom right, ${currentTheme.color}1A, white, #f0fdf4)` }}>
      {/* Sticky Top Header & Navigation */}
      <div className="sticky top-[74px] z-[999] mb-[100px] bg-white/95 backdrop-blur-xl border-b shadow-md transition-all duration-300 rounded-xl px-6 py-2" style={{ borderColor: `${currentTheme.color}33` }}>
        {/* Header */}
        <div className="flex items-center gap-4 mb-4">
          <button
            onClick={() => navigate('/products/list')}
            className="p-2 hover:bg-white rounded-lg transition-colors"
          >
            <ArrowLeft className="w-6 h-6 text-gray-600" />
          </button>
          <h1 className="text-2xl desktop:text-3xl font-bold text-gray-800">
            {isEditMode ? 'Edit Product' : 'Add new product'}
          </h1>

          {/* Progress Bar */}
          <div className="flex-1 max-w-xs ml-auto hidden md:block">
            <div className="flex justify-between items-center mb-1">
              <span className="text-xs font-bold uppercase" style={{ color: currentTheme.color }}>Form Completion</span>
              <span className="text-xs font-bold" style={{ color: currentTheme.color }}>{progress}%</span>
            </div>
            <div className="w-full bg-gray-200 rounded-full h-2 overflow-hidden">
              <div
                className="h-full transition-all duration-500 ease-out"
                style={{ width: `${progress}%`, backgroundColor: currentTheme.color }}
              />
            </div>
          </div>
        </div>

        {/* Horizontal Premium Navigation Tabs (X-axis) */}
        <div className="flex overflow-x-auto no-scrollbar gap-2 py-1">
          {[
            { id: 'details', label: 'Product Details', icon: LayoutGrid },
            { id: 'content', label: 'Product Content', icon: FileText },
            { id: 'benefits', label: 'Product Benefits', icon: BadgeCheck },
            { id: 'pricing', label: 'Pricing & Tax', icon: IndianRupee },
            { id: 'faq', label: 'FAQ Section', icon: HelpCircle },
          ].map((item) => (
            <button
              key={item.id}
              onClick={() => scrollToSection(item.id)}
              className={`flex-1 min-w-[140px] flex items-center justify-center gap-3 px-4 py-3 rounded-xl transition-all group relative ${activeTab === item.id
                ? 'text-white shadow-lg'
                : 'text-gray-500'
                }`}
              style={{
                backgroundColor: activeTab === item.id ? currentTheme.color : undefined,
                color: activeTab === item.id ? 'white' : undefined,
                boxShadow: activeTab === item.id ? `0 10px 15px -3px ${currentTheme.color}33` : undefined
              }}
            >
              <item.icon className={`w-5 h-5 transition-transform duration-300 ${activeTab === item.id ? 'scale-110' : 'group-hover:scale-110'}`} />
              <span className="text-xs font-bold uppercase tracking-wider">{item.label}</span>
              {activeTab === item.id && (
                <span className="absolute -bottom-1 left-1/2 -translate-x-1/2 w-8 h-1 bg-white rounded-full opacity-50" />
              )}
            </button>
          ))}
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Left Column - Fixed Info */}
        <div className="space-y-6">
          {/* Product Type Selection */}
          <div className="bg-white rounded-xl shadow-sm p-6">
            <h3 className="text-sm font-bold text-gray-400 uppercase tracking-wider mb-4">Product Type</h3>
            <div className="grid grid-cols-2 gap-3">
              <button
                onClick={() => setProductType('product')}
                className={`p-3 rounded-lg border-2 transition-all flex flex-col items-center gap-2 ${productType === 'product'
                  ? 'shadow-sm'
                  : 'border-gray-100 hover:border-gray-200 text-gray-500'
                  }`}
                style={{
                  borderColor: productType === 'product' ? currentTheme.color : undefined,
                  backgroundColor: productType === 'product' ? `${currentTheme.color}1A` : undefined,
                  color: productType === 'product' ? currentTheme.color : undefined
                }}
              >
                <Package className="w-6 h-6" />
                <span className="text-sm font-bold">Single Product</span>
              </button>
              <button
                onClick={() => {
                  setProductType('kit');
                  if (selectedProducts.length === 0) setIsKitStarted(false);
                }}
                className={`p-3 rounded-lg border-2 transition-all flex flex-col items-center gap-2 ${productType === 'kit'
                  ? 'shadow-sm'
                  : 'border-gray-100 hover:border-gray-200 text-gray-500'
                  }`}
                style={{
                  borderColor: productType === 'kit' ? currentTheme.color : undefined,
                  backgroundColor: productType === 'kit' ? `${currentTheme.color}1A` : undefined,
                  color: productType === 'kit' ? currentTheme.color : undefined
                }}
              >
                <Plus className="w-6 h-6" />
                <span className="text-sm font-bold">Kit / Combo</span>
              </button>
            </div>
          </div>

          {/* Image Upload - Reverted to Sidebar */}
          <div className="bg-white rounded-xl shadow-sm p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-sm font-bold text-gray-400 uppercase tracking-wider">Product Images</h3>
              <span className="text-xs text-gray-400">{productImages.length}/5 Images</span>
            </div>
            <div
              className={`border-2 border-dashed rounded-lg p-8 text-center transition-all cursor-pointer ${isDragging ? 'shadow-sm' : 'border-gray-300 bg-gray-50'
                }`}
              style={{
                borderColor: isDragging ? currentTheme.color : undefined,
                backgroundColor: isDragging ? `${currentTheme.color}1A` : undefined,
                transform: isDragging ? 'scale(1.02)' : undefined
              }}
              onDragOver={handleDragOver}
              onDragLeave={handleDragLeave}
              onDrop={handleDrop}
              onClick={() => document.getElementById('fileInput').click()}
            >
              <Upload className={`w-12 h-12 mx-auto mb-2 transition-transform ${isDragging ? 'scale-110' : ''}`} style={{ color: isDragging ? currentTheme.color : '#9ca3af' }} />
              <p className="text-sm font-bold text-gray-600">Drag & Drop images here</p>
              <p className="text-xs text-gray-400 mt-1">or click to browse files</p>
              <input
                id="fileInput"
                type="file"
                multiple
                accept="image/*"
                onChange={handleFileUpload}
                className="hidden"
              />
            </div>

            {productImages.length > 0 && (
              <div className="grid grid-cols-3 gap-2 mt-4">
                {productImages.map((img, index) => (
                  <div key={index} className="relative group aspect-square rounded-lg overflow-hidden border border-gray-100">
                    <img src={img} alt="" className="w-full h-full object-cover" />
                    <button
                      onClick={() => handleRemoveImage(index)}
                      className="absolute top-1 right-1 p-1 bg-red-500 text-white rounded-full opacity-0 group-hover:opacity-100 transition-opacity"
                    >
                      <X className="w-3 h-3" />
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>



        </div>


        {/* Right Column - Tabs */}
        <div className="lg:col-span-2">
          {(productType === 'product' || (productType === 'kit' && isKitStarted)) ? (
            <div className="space-y-6">
              {/* Product Details Tab */}
              {activeTab === 'details' && (
                <div ref={detailsRef} className="bg-white rounded-xl shadow-sm p-6 animate-fadeIn">
                  <h2 className="text-lg font-bold text-gray-800 mb-4">Product details</h2>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-bold text-gray-700 mb-1">
                        Title
                      </label>
                      <input
                        type="text"
                        value={formData.title}
                        onChange={(e) => handleInputChange('title', e.target.value)}
                        placeholder="Enter Product Title"
                        className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 ${errors.title ? 'border-red-500' : 'border-gray-300'}`}
                      />
                      {errors.title && <p className="text-red-500 text-xs mt-1">{errors.title}</p>}
                    </div>
                    <div>
                      <label className="block text-sm font-bold text-gray-700 mb-1">
                        Name
                      </label>
                      <input
                        type="text"
                        value={formData.name}
                        onChange={(e) => handleInputChange('name', e.target.value)}
                        placeholder="Enter Product Name"
                        className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 ${errors.name ? 'border-red-500' : 'border-gray-300'}`}
                      />
                      {errors.name && <p className="text-red-500 text-xs mt-1">{errors.name}</p>}
                    </div>

                    {productType === 'kit' && (
                      <div className="md:col-span-2 p-4 rounded-xl border mt-2" style={{ backgroundColor: `${currentTheme.color}0D`, borderColor: `${currentTheme.color}33` }}>
                        <div className="flex items-center justify-between mb-4">
                          <h3 className="text-sm font-bold uppercase tracking-wider flex items-center gap-2" style={{ color: currentTheme.color }}>
                            <Plus className="w-4 h-4" /> Kit Composition
                          </h3>
                        </div>

                        <div className="space-y-4">
                          <div ref={searchContainerRef}>
                            <div className="flex items-center justify-between mb-2">
                              <label className="block text-xs font-bold text-gray-500 uppercase">Search & Select Products</label>
                              <button
                                onClick={() => {
                                  setTempSelectedIds([]);
                                  setProductSearchTerm(''); // Clear search to hide inline results
                                  setShowProductSelectModal(true);
                                }}
                                className="text-xs font-bold flex items-center gap-1 transition-colors"
                                style={{ color: currentTheme.color }}
                              >
                                <LayoutGrid className="w-3 h-3" /> Bulk Select
                              </button>
                            </div>
                            <div className="relative">
                              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                              <input
                                type="text"
                                placeholder="Search products by name..."
                                value={productSearchTerm}
                                onChange={(e) => setProductSearchTerm(e.target.value)}
                                className="w-full pl-10 pr-4 py-2.5 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 bg-white text-sm"
                                style={{ focusRingColor: currentTheme.color }}
                              />
                            </div>

                            {/* Search Results / Multi-select Area */}
                            {productSearchTerm.trim().length > 0 && (
                              <div className="mt-2 bg-white border border-gray-100 rounded-xl shadow-xl max-h-[300px] overflow-y-auto no-scrollbar z-20 relative">
                                {availableProducts
                                  .filter(p =>
                                    (p.productName || p.name || '').toLowerCase().includes(productSearchTerm.toLowerCase()) &&
                                    !selectedProducts.some(sp => sp.productId === p.id)
                                  )
                                  .map(product => (
                                    <div
                                      key={product.id}
                                      onClick={() => {
                                        setSelectedProducts(prev => [...prev, {
                                          id: Date.now() + Math.random(),
                                          productId: product.id,
                                          name: product.productName || product.name,
                                          image: product.imageUrl || product.image,
                                          price: product.price || product.mrp,
                                          quantity: 1
                                        }]);
                                        setProductSearchTerm('');
                                      }}
                                      className="p-3 cursor-pointer flex items-center justify-between border-b border-gray-50 last:border-0 transition-colors"
                                      style={{ '--hover-bg': `${currentTheme.color}1A` }}
                                      onMouseEnter={(e) => e.currentTarget.style.backgroundColor = 'var(--hover-bg)'}
                                      onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
                                    >
                                      <div className="flex items-center gap-3">
                                        <div className="w-8 h-8 bg-gray-50 rounded border flex items-center justify-center overflow-hidden">
                                          {product.imageUrl || product.image ? (
                                            <img src={product.imageUrl || product.image} alt="" className="w-full h-full object-cover" />
                                          ) : (
                                            <Package className="w-4 h-4 text-gray-300" />
                                          )}
                                        </div>
                                        <div>
                                          <p className="text-sm font-bold text-gray-800">{product.productName || product.name}</p>
                                          <p className="text-xs text-gray-400">₹{product.price || product.mrp}</p>
                                        </div>
                                      </div>
                                      <button className="font-bold text-xs px-2 py-1 rounded-md transition-colors" style={{ backgroundColor: `${currentTheme.color}1A`, color: currentTheme.color }}>
                                        + Add
                                      </button>
                                    </div>
                                  ))}
                                {availableProducts.filter(p =>
                                  (p.productName || p.name || '').toLowerCase().includes(productSearchTerm.toLowerCase()) &&
                                  !selectedProducts.some(sp => sp.productId === p.id)
                                ).length === 0 && (
                                    <div className="p-8 text-center text-gray-400">
                                      <Info className="w-8 h-8 mx-auto mb-2 opacity-20" />
                                      <p className="text-sm">No new products found</p>
                                    </div>
                                  )}
                              </div>
                            )}
                          </div>

                          {selectedProducts.length > 0 && (
                            <div>
                              <div className="flex items-center justify-between mb-2">
                                <label className="block text-xs font-bold text-gray-500 uppercase">Selected items ({selectedProducts.length})</label>
                                <button
                                  onClick={() => setSelectedProducts([])}
                                  className="text-xs text-red-500 hover:underline font-bold"
                                >
                                  Clear All
                                </button>
                              </div>
                              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                                {selectedProducts.map((product) => (
                                  <div key={product.id} className="p-3 bg-white border rounded-xl flex items-center justify-between group" style={{ borderColor: `${currentTheme.color}33` }}>
                                    <div className="flex items-center gap-3">
                                      <div className="w-10 h-10 bg-gray-50 rounded-lg border border-gray-100 flex items-center justify-center overflow-hidden">
                                        {product.image ? (
                                          <img src={product.image} alt="" className="w-full h-full object-cover" />
                                        ) : (
                                          <Package className="w-5 h-5 text-gray-300" />
                                        )}
                                      </div>
                                      <div>
                                        <p className="text-sm font-bold text-gray-800 leading-none mb-1">{product.name}</p>
                                        <p className="text-xs text-gray-400">₹{product.price}</p>
                                      </div>
                                    </div>

                                    <div className="flex items-center gap-3">
                                      <div className="flex items-center bg-gray-50 rounded-lg border border-gray-100 p-1">
                                        <button
                                          onClick={() => handleProductKitChange(product.id, 'quantity', Math.max(1, parseInt(product.quantity) - 1))}
                                          className="w-6 h-6 flex items-center justify-center text-gray-400 transition-colors"
                                          style={{ '--hover-color': currentTheme.color }}
                                          onMouseEnter={(e) => e.currentTarget.style.color = 'var(--hover-color)'}
                                          onMouseLeave={(e) => e.currentTarget.style.color = 'rgb(156 163 175)'}
                                        >
                                          -
                                        </button>
                                        <span className="w-8 text-center text-xs font-bold text-gray-700">{product.quantity}</span>
                                        <button
                                          onClick={() => handleProductKitChange(product.id, 'quantity', parseInt(product.quantity) + 1)}
                                          className="w-6 h-6 flex items-center justify-center text-gray-400 transition-colors"
                                          style={{ '--hover-color': currentTheme.color }}
                                          onMouseEnter={(e) => e.currentTarget.style.color = 'var(--hover-color)'}
                                          onMouseLeave={(e) => e.currentTarget.style.color = 'rgb(156 163 175)'}
                                        >
                                          +
                                        </button>
                                      </div>
                                      <button
                                        onClick={() => handleRemoveProductFromKit(product.id)}
                                        className="p-1.5 text-gray-400 hover:text-red-500 hover:bg-red-50 rounded-lg transition-colors"
                                      >
                                        <X className="w-4 h-4" />
                                      </button>
                                    </div>
                                  </div>
                                ))}
                              </div>
                            </div>
                          )}
                        </div>
                      </div>
                    )}

                    <div>
                      <label className="block text-sm font-bold text-gray-700 mb-1">
                        Sub Category
                      </label>
                      <select
                        value={formData.subCategory}
                        onChange={(e) => handleInputChange('subCategory', e.target.value)}
                        disabled={!formData.category}
                        className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 bg-white
                          ${errors.subCategory ? 'border-red-500' : 'border-gray-300'}
                          ${!formData.category ? 'bg-gray-50 text-gray-400 cursor-not-allowed' : ''}`}
                      >
                        <option value="">
                          {!formData.category ? 'Select category first' : 'Select Sub Category'}
                        </option>
                        {subCategories.map((sub, i) => (
                          <option key={i} value={sub}>{sub}</option>
                        ))}
                      </select>
                      {errors.subCategory && <p className="text-red-500 text-xs mt-1">{errors.subCategory}</p>}
                    </div>
                    <div>
                      <label className="block text-sm font-bold text-gray-700 mb-1">
                        Category
                      </label>
                      <select
                        value={formData.category}
                        onChange={(e) => handleInputChange('category', e.target.value)}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2"
                      >
                        <option value="">Select Category</option>
                        {Array.isArray(categories) && categories.filter(Boolean).map((cat, i) => {
                          const catName = typeof cat === 'string' ? cat : (cat?.categoryName || cat?.name || '');
                          const catId = cat?.id || i;
                          return <option key={catId} value={catName}>{catName}</option>;
                        })}
                      </select>
                    </div>
                    <div className="md:col-span-2">
                      <label className="block text-sm font-bold text-gray-700 mb-1">
                        Product URL
                      </label>
                      <input
                        type="text"
                        value={formData.url}
                        onChange={(e) => handleInputChange('url', e.target.value)}
                        placeholder="https://example.com/product"
                        className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 ${errors.url ? 'border-red-500' : 'border-gray-300'}`}
                      />
                      {errors.url && <p className="text-red-500 text-xs mt-1">{errors.url}</p>}
                    </div>
                  </div>
                  <div className="mt-6 flex justify-end">
                    <button onClick={() => setActiveTab('content')} className="px-6 py-2 text-white rounded-lg font-bold transition-colors" style={{ backgroundColor: currentTheme.color }}>Continue to Content</button>
                  </div>
                </div>
              )}

              {/* Product Content */}
              {activeTab === 'content' && (
                <div ref={contentRef} className="bg-white rounded-xl shadow-sm p-6 scroll-mt-6">
                  <h2 className="text-lg font-bold text-gray-800 mb-4">Product content</h2>
                  <div className="space-y-4">
                    <div>
                      <label className="block text-sm font-bold text-gray-700 mb-1">
                        Package size
                      </label>
                      <input
                        type="text"
                        value={formData.packageSize}
                        onChange={(e) => handleInputChange('packageSize', e.target.value)}
                        placeholder="750 mL"
                        className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 ${errors.packageSize ? 'border-red-500' : 'border-gray-300'}`}
                      />
                      {errors.packageSize && <p className="text-red-500 text-xs mt-1">{errors.packageSize}</p>}
                    </div>
                    <div>
                      <label className="block text-sm font-bold text-gray-700 mb-1">
                        Ratings
                      </label>
                      <div className="flex items-center gap-4">
                        <div className="flex gap-1 bg-gray-50/50 p-2 rounded-lg border border-gray-100">
                          {[0, 1, 2, 3, 4].map((i) => (
                            <RatingStar
                              key={i}
                              index={i}
                              rating={parseFloat(formData.ratings || 0)}
                              onClick={(val) => handleInputChange('ratings', val)}
                            />
                          ))}
                        </div>
                        <input
                          type="number"
                          step="0.1"
                          min="0"
                          max="5"
                          value={formData.ratings}
                          onChange={(e) => handleInputChange('ratings', e.target.value)}
                          className="w-20 px-2 py-1 border border-gray-300 rounded-lg focus:outline-none focus:ring-1 text-sm font-bold"
                          style={{ focusRingColor: currentTheme.color }}
                          placeholder="4.5"
                        />
                      </div>
                      {errors.ratings && <p className="text-red-500 text-xs mt-1">{errors.ratings}</p>}
                    </div>
                    <div>
                      <label className="block text-sm font-bold text-gray-700 mb-1">About Product</label>
                      <input
                        type="text"
                        value={formData.aboutProduct}
                        onChange={(e) => handleInputChange('aboutProduct', e.target.value)}
                        placeholder="Brief description"
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-bold text-gray-700 mb-1">Description <span className="text-red-500">*</span></label>
                      <textarea
                        value={formData.description}
                        onChange={(e) => handleInputChange('description', e.target.value)}
                        placeholder="Enter product description"
                        rows={4}
                        className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 ${errors.description ? 'border-red-500' : 'border-gray-300'}`}
                      />
                      {errors.description && <p className="text-red-500 text-xs mt-1">{errors.description}</p>}
                    </div>
                  </div>
                  <div className="flex justify-between pt-6 border-t border-gray-100 mt-6">
                    <button onClick={() => setActiveTab('details')} className="px-6 py-2 border border-gray-300 text-gray-700 rounded-lg font-bold hover:bg-gray-50 transition-colors">Back</button>
                    <button onClick={() => setActiveTab('benefits')} className="px-6 py-2 text-white rounded-lg font-bold transition-colors" style={{ backgroundColor: currentTheme.color }}>Continue to Benefits</button>
                  </div>
                </div>
              )}

              {/* Product Benefits Tab */}
              {activeTab === 'benefits' && (
                <div ref={benefitsRef} className="bg-white rounded-xl shadow-sm p-6 animate-fadeIn scroll-mt-6">
                  <h2 className="text-lg font-bold text-gray-800 mb-4">Product benefits</h2>
                  <div className="space-y-6">
                    <div>
                      <label className="block text-sm font-bold text-gray-700 mb-2">Attributes</label>
                      <div className="flex flex-wrap gap-2 mb-3">
                        {formData.attributes.map((attr, index) => (
                          <span key={index} className="px-3 py-1 bg-green-100 text-green-700 rounded-full text-sm flex items-center gap-1 font-bold">
                            {attr}
                            <X className="w-3 h-3 cursor-pointer" onClick={() => handleRemoveAttribute(index)} />
                          </span>
                        ))}
                      </div>
                      <div className="flex gap-2">
                        <input
                          type="text"
                          value={tempAttribute}
                          onChange={(e) => setTempAttribute(e.target.value)}
                          onKeyPress={(e) => e.key === 'Enter' && handleAddAttribute()}
                          placeholder="Add attribute"
                          className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2"
                          style={{ focusRingColor: currentTheme.color }}
                        />
                        <button onClick={handleAddAttribute} className="px-4 py-2 text-white rounded-lg font-bold" style={{ backgroundColor: currentTheme.color }}>Add</button>
                      </div>
                    </div>

                    <div>
                      <label className="block text-sm font-bold text-gray-700 mb-2">Ingredients</label>
                      {formData.ingredients.map((ingredient, index) => (
                        <div key={index} className="grid grid-cols-2 gap-3 mb-3 p-3 border border-gray-100 rounded-xl bg-gray-50 relative group">
                          <button
                            onClick={() => handleRemoveIngredient(index)}
                            className="absolute -top-2 -right-2 p-1 bg-red-500 text-white rounded-full opacity-0 group-hover:opacity-100 transition-opacity z-10"
                          >
                            <X className="w-3 h-3" />
                          </button>

                          <div className="flex items-center gap-2">
                            <input
                              type="text"
                              value={ingredient.heading}
                              onChange={(e) => handleIngredientChange(index, 'heading', e.target.value)}
                              placeholder="Heading"
                              className="flex-1 px-3 py-2 bg-white border border-gray-200 rounded-lg focus:outline-none focus:ring-2 text-sm font-bold"
                              style={{ focusRingColor: currentTheme.color }}
                            />
                            <div className="relative">
                              <input
                                type="file"
                                id={`ing-img-${index}`}
                                className="hidden"
                                onChange={(e) => handleIngredientChange(index, 'imgFile', e.target.files[0])}
                              />
                              <label
                                htmlFor={`ing-img-${index}`}
                                className="w-12 h-12 border border-gray-200 rounded-lg cursor-pointer hover:bg-gray-50 bg-white flex items-center justify-center overflow-hidden transition-all shadow-sm shrink-0"
                              >
                                {ingredient.previewUrl || ingredient.imageUrl ? (
                                  <img
                                    src={ingredient.previewUrl || ingredient.imageUrl}
                                    alt=""
                                    className="w-full h-full object-cover"
                                    onError={(e) => {
                                      // If the current src (likely server) fails, keep trying with prefix or hide
                                      if (ingredient.imageUrl && !ingredient.imageUrl.startsWith('http') && !ingredient.imageUrl.startsWith('blob:')) {
                                        e.target.src = `${ASSET_URL}/${ingredient.imageUrl.replace(/^\//, '')}`;
                                      }
                                    }}
                                  />
                                ) : (
                                  <Upload className="w-5 h-5 text-gray-400" />
                                )}
                              </label>
                            </div>
                          </div>
                          <input
                            type="text"
                            value={ingredient.content}
                            onChange={(e) => handleIngredientChange(index, 'content', e.target.value)}
                            placeholder="Content"
                            className="px-3 py-2 bg-white border border-gray-200 rounded-lg focus:outline-none focus:ring-2 text-sm"
                            style={{ focusRingColor: currentTheme.color }}
                          />
                        </div>
                      ))}
                      <button onClick={handleAddIngredient} className="flex items-center gap-2 text-sm font-bold transition-colors" style={{ color: currentTheme.color }}>
                        <Plus className="w-4 h-4" /> Add Ingredient
                      </button>
                    </div>

                  </div>
                  <div className="flex justify-between pt-6 border-t border-gray-100 mt-6">
                    <button onClick={() => setActiveTab('content')} className="px-6 py-2 border border-gray-300 text-gray-700 rounded-lg font-bold hover:bg-gray-50 transition-colors">Back</button>
                    <button onClick={() => setActiveTab('pricing')} className="px-6 py-2 text-white rounded-lg font-bold transition-colors" style={{ backgroundColor: currentTheme.color }}>Continue to Pricing</button>
                  </div>
                </div>
              )}

              {/* Pricing Tab */}
              {activeTab === 'pricing' && (
                <div ref={pricingRef} className="bg-white rounded-xl shadow-sm p-6 animate-fadeIn scroll-mt-6">
                  <div className="flex items-center justify-between mb-4">
                    <h2 className="text-lg font-bold text-gray-800">Pricing & Tax</h2>
                    <div className="flex items-center gap-2 px-3 py-1.5 bg-yellow-50 text-yellow-700 rounded-lg border border-yellow-200">
                      <Calculator className="w-4 h-4" />
                      <span className="text-xs font-bold uppercase tracking-wider">Auto-Calculator</span>
                    </div>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
                    <div className="p-4 rounded-xl border text-center" style={{ backgroundColor: `${currentTheme.color}0D`, borderColor: `${currentTheme.color}33` }}>
                      <p className="text-[10px] uppercase font-bold mb-1" style={{ color: currentTheme.color }}>Selling Price</p>
                      <p className="text-2xl font-bold" style={{ color: currentTheme.color }}>₹{priceStats.sellingPrice}</p>
                    </div>
                    <div className="bg-green-50 p-4 rounded-xl border border-green-100 text-center">
                      <p className="text-[10px] text-green-600 uppercase font-bold mb-1">Customer Saving</p>
                      <p className="text-2xl font-bold text-green-700">₹{priceStats.saving}</p>
                    </div>
                    <div className="bg-orange-50 p-4 rounded-xl border border-orange-100 text-center">
                      <p className="text-[10px] text-orange-600 uppercase font-bold mb-1">Discount Active</p>
                      <p className="text-2xl font-bold text-orange-700">{formData.b2cDiscount || 0}% OFF</p>
                    </div>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-bold text-gray-700 mb-1">MRP</label>
                      <input type="text" value={formData.mrp} onChange={(e) => handleInputChange('mrp', e.target.value)} placeholder="₹ 999" className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 ${errors.mrp ? 'border-red-500' : 'border-gray-300'}`} />
                      {errors.mrp && <p className="text-red-500 text-xs mt-1">{errors.mrp}</p>}
                    </div>
                    <div>
                      <label className="block text-sm font-bold text-gray-700 mb-1 flex items-center gap-1">
                        HSN Code
                        <HelpCircle className="w-3.5 h-3.5 text-gray-400 cursor-help" title="Harmonized System of Nomenclature code for tax classification" />
                      </label>
                      <input type="text" value={formData.hsnCode} onChange={(e) => handleInputChange('hsnCode', e.target.value)} placeholder="HSN Code" className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 ${errors.hsnCode ? 'border-red-500' : 'border-gray-300'}`} />
                      {errors.hsnCode && <p className="text-red-500 text-xs mt-1">{errors.hsnCode}</p>}
                    </div>
                    <div>
                      <label className="block text-sm font-bold text-gray-700 mb-1 flex items-center gap-1">
                        SKU number
                        <HelpCircle className="w-3.5 h-3.5 text-gray-400 cursor-help" title="Stock Keeping Unit - unique identifier for your product" />
                      </label>
                      <input type="text" value={formData.sku} onChange={(e) => handleInputChange('sku', e.target.value)} placeholder="SKU No." className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 ${errors.sku ? 'border-red-500' : 'border-gray-300'}`} />
                      {errors.sku && <p className="text-red-500 text-xs mt-1">{errors.sku}</p>}
                    </div>
                    <div>
                      <label className="block text-sm font-bold text-gray-700 mb-1">Tax <span className="text-red-500">*</span></label>
                      <input type="text" value={formData.tax} onChange={(e) => handleInputChange('tax', e.target.value)} placeholder="Tax %" className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2" />
                    </div>
                    <div>
                      <label className="block text-sm font-bold text-gray-700 mb-1">Batch number <span className="text-red-500">*</span></label>
                      <input type="text" value={formData.batchNumber} onChange={(e) => handleInputChange('batchNumber', e.target.value)} placeholder="Batch No." className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 ${errors.batchNumber ? 'border-red-500' : 'border-gray-300'}`} />
                    </div>
                    <div>
                      <label className="block text-sm font-bold text-gray-700 mb-1">Manufacturing date <span className="text-red-500">*</span></label>
                      <input type="date" value={formData.manufactoryDate} max={new Date().toISOString().split('T')[0]} onChange={(e) => handleInputChange('manufactoryDate', e.target.value)} className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 ${errors.manufactoryDate ? 'border-red-500' : 'border-gray-300'}`} />
                    </div>
                    <div>
                      <label className="block text-sm font-bold text-gray-700 mb-1">Expiry date <span className="text-red-500">*</span></label>
                      <input type="date" value={formData.expiryDate} onChange={(e) => handleInputChange('expiryDate', e.target.value)} className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 ${errors.expiryDate ? 'border-red-500' : 'border-gray-300'}`} />
                    </div>
                    <div>
                      <label className="block text-sm font-bold text-gray-700 mb-1">B2C Discount (%)</label>
                      <input type="text" value={formData.b2cDiscount} onChange={(e) => handleInputChange('b2cDiscount', e.target.value)} placeholder="0" className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2" style={{ focusRingColor: currentTheme.color }} />
                    </div>
                  </div>
                  <div className="flex justify-between pt-6 border-t border-gray-100 mt-6">
                    <button onClick={() => setActiveTab('benefits')} className="px-6 py-2 border border-gray-300 text-gray-700 rounded-lg font-bold hover:bg-gray-50 transition-colors">Back</button>
                    <button onClick={() => setActiveTab('faq')} className="px-6 py-2 text-white rounded-lg font-bold transition-colors" style={{ backgroundColor: currentTheme.color }}>Continue to FAQ</button>
                  </div>
                </div>
              )}

              {/* FAQ Tab */}
              {activeTab === 'faq' && (
                <div ref={faqRef} className="bg-white rounded-xl shadow-sm p-6 animate-fadeIn scroll-mt-6">
                  <h2 className="text-lg font-bold text-gray-800 mb-4">FAQ Section</h2>
                  <div className="space-y-4">
                    {formData.faqs.map((faq, index) => (
                      <div key={index} className="p-4 border border-gray-100 rounded-xl bg-gray-50 space-y-3">
                        <input
                          type="text"
                          value={faq.question}
                          onChange={(e) => handleFAQChange(index, 'question', e.target.value)}
                          placeholder="Question"
                          className="w-full px-3 py-2 bg-white border border-gray-200 rounded-lg focus:outline-none focus:ring-2 font-bold"
                          style={{ focusRingColor: currentTheme.color }}
                        />
                        <textarea
                          value={faq.answer}
                          onChange={(e) => handleFAQChange(index, 'answer', e.target.value)}
                          placeholder="Answer"
                          rows={3}
                          className="w-full px-3 py-2 bg-white border border-gray-200 rounded-lg focus:outline-none focus:ring-2"
                          style={{ focusRingColor: currentTheme.color }}
                        />
                      </div>
                    ))}
                    <button onClick={handleAddFAQ} className="flex items-center gap-2 text-sm font-bold transition-colors" style={{ color: currentTheme.color }}>
                      <Plus className="w-4 h-4" /> Add FAQ
                    </button>
                  </div>
                  <div className="flex justify-between pt-6 border-t border-gray-100 mt-6">
                    <button onClick={() => setActiveTab('pricing')} className="px-6 py-2 border border-gray-300 text-gray-700 rounded-lg font-bold hover:bg-gray-50 transition-colors">Back</button>
                    <div className="flex gap-3">
                      <button onClick={() => navigate('/products/list')} className="px-6 py-2 text-gray-500 font-bold">Cancel</button>
                      <button
                        onClick={handleSubmit}
                        disabled={loading}
                        className="px-8 py-2 text-white rounded-lg font-bold shadow-lg transition-transform active:scale-95 disabled:opacity-50"
                        style={{ backgroundColor: currentTheme.color }}
                      >
                        {loading ? 'Saving...' : (isEditMode ? 'Update Product' : 'Save Product')}
                      </button>
                    </div>
                  </div>
                </div>
              )}
            </div>
          ) : (
            <div className="bg-white rounded-xl shadow-sm p-12 text-center flex flex-col items-center justify-center space-y-4">
              <div className="w-20 h-20 rounded-full flex items-center justify-center" style={{ backgroundColor: `${currentTheme.color}1A`, color: currentTheme.color }}>
                <Plus className="w-10 h-10" />
              </div>
              <h3 className="text-xl font-bold text-gray-800">Select Kit Products</h3>
              <p className="text-gray-500 max-w-xs">A kit requires at least one product. Start adding your products below.</p>
              <button
                onClick={() => setIsKitStarted(true)}
                className="px-6 py-3 text-white rounded-xl font-bold shadow-md transition-all mt-4"
                style={{ backgroundColor: currentTheme.color }}
              >
                Start Adding Products
              </button>
            </div>
          )}
        </div>
      </div>

      {
        showProductSelectModal && (
          <div className="fixed inset-0 z-[1001] flex items-center justify-center bg-black/60 backdrop-blur-sm p-4">
            <div className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl max-h-[85vh] flex flex-col overflow-hidden animate-scaleIn">
              <div className="p-6 border-b border-gray-100 flex justify-between items-center bg-gray-50/50">
                <h2 className="text-xl font-bold text-gray-800 flex items-center gap-2">
                  <Package className="w-6 h-6" style={{ color: currentTheme.color }} />
                  Select Products to Add
                </h2>
                <button
                  onClick={() => {
                    setShowProductSelectModal(false);
                    setTempSelectedIds([]);
                    setProductSearchTerm(''); // Clear search on close
                  }}
                  className="p-2 hover:bg-gray-100 rounded-full transition-colors"
                >
                  <X className="w-6 h-6 text-gray-400" />
                </button>
              </div>

              <div className="p-6 space-y-4 flex-1 flex flex-col min-h-0">
                <div className="relative">
                  <Search className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400 w-5 h-5" />
                  <input
                    type="text"
                    placeholder="Search products by name..."
                    value={productSearchTerm}
                    onChange={(e) => setProductSearchTerm(e.target.value)}
                    className="w-full pl-12 pr-4 py-3 bg-gray-50 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 font-medium"
                    style={{ focusRingColor: currentTheme.color }}
                  />
                </div>

                <div className="flex-1 overflow-y-auto pr-2 space-y-2 custom-scrollbar">
                  {availableProducts
                    .filter(p => (p.productName || p.name || '').toLowerCase().includes(productSearchTerm.toLowerCase()))
                    .map(product => {
                      const isSelected = tempSelectedIds.includes(product.id) || selectedProducts.some(sp => sp.productId === product.id);
                      return (
                        <div
                          key={product.id}
                          onClick={() => {
                            if (selectedProducts.some(sp => sp.productId === product.id)) return;

                            setTempSelectedIds(prev =>
                              prev.includes(product.id)
                                ? prev.filter(id => id !== product.id)
                                : [...prev, product.id]
                            );
                          }}
                          className={`flex items-center gap-4 p-4 border rounded-xl cursor-pointer transition-all group ${isSelected
                            ? 'ring-1'
                            : 'bg-white border-gray-100 hover:bg-gray-50 hover:border-gray-200'
                            } ${selectedProducts.some(sp => sp.productId === product.id) ? 'opacity-50 cursor-not-allowed' : ''}`}
                          style={{
                            backgroundColor: isSelected ? `${currentTheme.color}1A` : undefined,
                            borderColor: isSelected ? `${currentTheme.color}4D` : undefined,
                            ringColor: isSelected ? `${currentTheme.color}4D` : undefined
                          }}
                        >
                          <div className={`w-6 h-6 rounded-md border-2 flex items-center justify-center transition-colors ${isSelected ? 'border-transparent' : 'border-gray-200'
                            }`}
                            style={{ backgroundColor: isSelected ? currentTheme.color : undefined }}>
                            {isSelected && <BadgeCheck className="w-4 h-4 text-white" />}
                          </div>
                          <div className="w-12 h-12 bg-white rounded-lg border border-gray-100 overflow-hidden">
                            {product.imageUrl ? <img src={product.imageUrl} alt="" className="w-full h-full object-cover" /> : <Package className="w-5 h-5 m-3 text-gray-200" />}
                          </div>
                          <div className="flex-1">
                            <h4 className="font-bold text-gray-800">{product.productName || product.name || 'Untitled'}</h4>
                            <p className="text-xs text-gray-400 font-medium tracking-wider">₹{product.price || product.mrp || 0}</p>
                          </div>
                          {selectedProducts.some(sp => sp.productId === product.id) && (
                            <span className="text-[10px] bg-gray-100 text-gray-500 px-2 py-1 rounded font-bold">Already Added</span>
                          )}
                        </div>
                      );
                    })}
                </div>
              </div>

              <div className="p-6 border-t border-gray-100 bg-gray-50 flex justify-between items-center">
                <p className="text-sm font-medium text-gray-500">
                  {tempSelectedIds.length} products selected
                </p>
                <div className="flex gap-3">
                  <button
                    onClick={() => {
                      setShowProductSelectModal(false);
                      setTempSelectedIds([]);
                      setProductSearchTerm(''); // Clear search on cancel
                    }}
                    className="px-6 py-2 text-gray-500 font-bold hover:bg-gray-100 rounded-lg transition-colors"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={() => {
                      const productsToAdd = availableProducts.filter(p => tempSelectedIds.includes(p.id));
                      setSelectedProducts(prev => [
                        ...prev,
                        ...productsToAdd.map(product => ({
                          id: Date.now() + Math.random(),
                          productId: product.id,
                          name: product.productName || product.name,
                          image: product.imageUrl || product.image,
                          price: product.price || product.mrp,
                          quantity: 1
                        }))
                      ]);
                      setShowProductSelectModal(false);
                      setTempSelectedIds([]);
                      setProductSearchTerm(''); // Clear search after adding
                      message.success(`Added ${productsToAdd.length} products to kit`);
                    }}
                    disabled={tempSelectedIds.length === 0}
                    className="px-8 py-2 text-white rounded-lg font-bold shadow-lg transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                    style={{ backgroundColor: currentTheme.color }}
                  >
                    Add Selected Products
                  </button>
                </div>
              </div>
            </div>
          </div>
        )
      }
    </div >
  );
};

export default AddProduct;
